import { useCallback, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { DashboardEmpty } from "../../components/dashboard/DashboardEmpty";
import { SwitcherTabStrip } from "../../components/dashboard/instance-switcher/SwitcherTabStrip";
import { FEATURE_FLAGS, flagEnabled, useAuth } from "../../hooks/useAuth";
import { useChannels } from "../../hooks/useChannels";
import { useClawMetrics } from "../../hooks/useClawMetrics";
import { useToolUsage } from "../../hooks/useToolUsage";
import { pausedState, tightestLimit } from "../../lib/toolUsage";
import { ToolCallsPausedBanner } from "../../components/dashboard/ToolCallsPausedBanner";
import { useIntegrations } from "../../hooks/useIntegrations";
import { channelSetupBlockedReason } from "../../lib/channelSetupGate";
import { buildRequiredApps } from "../../lib/requiredApps";
import { useAppChoiceGroups } from "../../hooks/useIntegrationsCatalog";
import { usePendingInstance } from "../../hooks/useOnboarding";
import {
  CONCIERGE,
  type DashboardConcierge,
  type DashboardSpend,
} from "./mock";
import { useDashboardData } from "./useDashboardData";
import { withDisk } from "./dashboardAdapters";
import { HeroSkeleton } from "./sections/HeroSkeleton";
import { InstanceHero } from "./sections/InstanceHero";
import { IntegrationsSection } from "./sections/Integrations";
import { MessageChannelsSection } from "./sections/MessageChannels";
import { ChannelReconnectBanner } from "../../components/dashboard/ChannelReconnectBanner";
import { MESSAGE_CHANNELS } from "../../config/messageChannels";
import { ResourcesSection } from "./sections/Resources";
import styles from "./DashboardOverview.module.css";

export const DashboardOverview = (): JSX.Element => {
  const { user } = useAuth();
  const { pending } = usePendingInstance();
  const { status, instances } = useDashboardData();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const fallback = instances[0];
  // ?instance=<tenantId> preselects a tab, e.g. admin "View as owner" from
  // the Instances table. Unknown ids fall back to the first instance.
  const [selectedId, setSelectedId] = useState<string>(() => searchParams.get("instance") ?? "");

  const currentInstance = useMemo(() => {
    const match = instances.find((i) => i.id === selectedId);
    return match ?? fallback ?? null;
  }, [instances, selectedId, fallback]);

  const tenantId = currentInstance?.id ?? null;

  const { metrics, refresh: refreshMetrics } = useClawMetrics(tenantId);
  const toolUsageEnabled = flagEnabled(user?.features, FEATURE_FLAGS.toolUsageDashboard);
  const { data: toolUsage } = useToolUsage(toolUsageEnabled ? tenantId : null);
  const toolCallsPaused = toolUsage === null || !toolUsageEnabled ? null : pausedState(toolUsage);
  const usageHref = tenantId === null ? "/dashboard/usage" : `/dashboard/usage?instance=${encodeURIComponent(tenantId)}`;

  const enrichedInstance = useMemo(
    () => (currentInstance === null ? null : withDisk(currentInstance, metrics?.disk ?? null)),
    [currentInstance, metrics],
  );

  // Channels can only be wired onto a running, unlocked instance (mirrors how
  // integrations require a live tenant before "Connect" does anything).
  const channelSetupBlocked = channelSetupBlockedReason(enrichedInstance);

  const spend: DashboardSpend | null = useMemo(() => {
    if (metrics?.ai == null) return null;
    return {
      spentUsd: metrics.ai.spentUsd,
      limitUsd: metrics.ai.limitUsd,
      periodLabel: metrics.ai.periodLabel,
    };
  }, [metrics]);

  const {
    integrations,
    status: integrationsStatus,
    error: integrationsError,
  } = useIntegrations(tenantId);

  const {
    channels,
    refetch: refreshChannels,
    request: requestChannel,
    cancelRequest: cancelChannelRequest,
  } = useChannels(tenantId);

  // The message channels strip always sits at the top, just above Integrations,
  // so what the user can already talk to their agent on is immediately visible.
  // Once at least one channel is active it collapses to a quiet bar; until then
  // it stays the prominent full onboarding grid.
  const hasConnectedChannel = useMemo(
    () => channels.some((c) => c.status === "active"),
    [channels],
  );

  // Channels the customer connected that have since come unlinked. While any
  // exist the strip stays expanded and a banner says so: the agent itself
  // can't tell anyone over a channel that is down.
  const unlinkedChannelNames = useMemo(
    () =>
      channels
        .filter((c) => c.status === "active" && c.needsReconnect)
        .map((c) => MESSAGE_CHANNELS.find((d) => d.slug === c.channel)?.name ?? c.channel),
    [channels],
  );
  const canLink = (enrichedInstance?.role ?? "OWNER") === "OWNER";

  // `?reconnect=whatsapp` opens the reconnect flow directly: a link support can
  // send, and what the banner's button does.
  const [reconnectRequest, setReconnectRequest] = useState<string | null>(() => searchParams.get("reconnect"));
  const handleReconnectHandled = useCallback(() => {
    setReconnectRequest(null);
    if (searchParams.has("reconnect")) {
      const next = new URLSearchParams(searchParams);
      next.delete("reconnect");
      setSearchParams(next, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  // Everything the tenant has actually plugged in belongs in the hero panel,
  // from either signal: the legacy lifecycle (`status` active/provisioning,
  // for admin/onboarding-wired channels) OR a live connection
  // (`connectionStatus`, for Composio/ServiceM8 connect flows, including
  // error/revoked so broken connections stay visible). A 'disconnected' row
  // (IP-206 soft delete) is intentionally excluded — the user removed it, so it
  // drops out of "Active integrations" and lives only in the catalog.
  // ActiveIntegrations derives a per-row badge from both signals.
  const heroIntegrations = useMemo(
    () =>
      integrations.filter(
        (i) =>
          i.status === "active" ||
          i.status === "provisioning" ||
          (i.connectionStatus !== null && i.connectionStatus !== "disconnected"),
      ),
    [integrations],
  );

  // IP-588: apps an admin pinned to this customer's invite, joined to what the
  // tenant has actually connected. Drives the locked screen's checklist and the
  // onboarding-call gate.
  const { groups: appGroups, status: appGroupsStatus } = useAppChoiceGroups();
  const pinnedTokens = useMemo(
    () => user?.requestedIntegrations ?? [],
    [user?.requestedIntegrations],
  );
  const requiredApps = useMemo(
    () => buildRequiredApps(pinnedTokens, integrations, appGroups),
    [pinnedTokens, integrations, appGroups],
  );
  // Both sources start empty, so an empty `requiredApps` means "not loaded
  // yet" just as often as it means "nothing outstanding" — the gate has to be
  // told which. A customer with no pinned tokens has nothing to wait for
  // (`user` is already resolved before the hero renders), so the common case
  // never sees a held gate.
  const requiredAppsResolved =
    pinnedTokens.length === 0 ||
    (integrationsStatus !== "loading" && appGroupsStatus !== "loading");

  const concierge: DashboardConcierge = useMemo(
    () => ({
      ...CONCIERGE,
      bookCallUrl: user?.bookOnboardingCallUrl ?? CONCIERGE.bookCallUrl,
      sessionsRemaining: currentInstance?.coachingSessionsRemaining ?? CONCIERGE.sessionsRemaining,
      unlimited: currentInstance?.unlimitedEntitlements ?? false,
    }),
    [user, currentInstance],
  );

  const userBudget = user?.userBudget ?? 1;
  // Shared instances (MANAGER/MEMBER role) don't consume the caller's
  // budget — only instances they own count against it.
  const ownedCount = instances.filter((i) => (i.role ?? "OWNER") === "OWNER").length;
  const launchNewEnabled = flagEnabled(user?.features, FEATURE_FLAGS.launchNewEnabled);
  const canLaunch = launchNewEnabled && ownedCount < userBudget;
  // Budget 0 = no purchase entitlement (a sharing collaborator): offer the
  // purchase path instead of the launch button.
  const showPurchaseCta = launchNewEnabled && userBudget === 0;
  // `||` not `??`: an explicitly empty purchase URL must not render an
  // anchor with href="" (it would open the SPA itself in a new tab).
  const purchaseUrl = user?.purchaseUrl || null;
  const launchTooltip = canLaunch
    ? undefined
    : `You've reached your ${userBudget}-instance limit. Delete an existing instance to launch another.`;

  const handleLaunchNew = (): void => {
    if (!canLaunch) return;
    navigate("/dashboard/onboarding");
  };

  const renderHeroRegion = (): JSX.Element => {
    if (status === "loading") {
      return <HeroSkeleton />;
    }
    if (enrichedInstance === null) {
      return (
        <DashboardEmpty
          pending={pending}
          bookCallUrl={user?.bookOnboardingCallUrl ?? null}
          canStart={userBudget > 0}
          purchaseUrl={purchaseUrl}
        />
      );
    }
    return (
      <>
        <SwitcherTabStrip
          instances={instances}
          currentId={enrichedInstance.id}
          onSelect={setSelectedId}
          {...(showPurchaseCta && purchaseUrl !== null
            ? { purchaseCta: { label: "Get your own OpenClaw", href: purchaseUrl } }
            : launchNewEnabled
              ? {
                  onLaunchNew: handleLaunchNew,
                  canLaunch,
                  ...(launchTooltip !== undefined ? { launchTooltip } : {}),
                }
              : {})}
        />
        {toolCallsPaused !== null && (
          <div className={styles.pausedBanner}>
            <ToolCallsPausedBanner paused={toolCallsPaused} usageHref={usageHref} />
          </div>
        )}
        <InstanceHero
          instance={enrichedInstance}
          spend={spend}
          {...(toolUsage !== null && toolUsageEnabled
            ? { toolCalls: { limit: tightestLimit(toolUsage), paused: toolCallsPaused?.allApps === true, href: usageHref } }
            : {})}
          concierge={concierge}
          activeIntegrations={heroIntegrations}
          requiredApps={requiredApps}
          requiredAppsResolved={requiredAppsResolved}
          onAiKeyUpdated={refreshMetrics}
          onProviderChanged={refreshMetrics}
        />
      </>
    );
  };

  const channelsSection = (
    <div className={styles.sectionWrapper}>
      <MessageChannelsSection
        channels={channels}
        integrations={integrations}
        tenantId={tenantId}
        setupBlockedReason={channelSetupBlocked}
        collapsible={hasConnectedChannel && unlinkedChannelNames.length === 0}
        onRequest={requestChannel}
        onCancelRequest={cancelChannelRequest}
        onChanged={refreshChannels}
        canLink={canLink}
        reconnectRequest={reconnectRequest}
        onReconnectHandled={handleReconnectHandled}
        notice={
          unlinkedChannelNames.length > 0 ? (
            <ChannelReconnectBanner
              channelNames={unlinkedChannelNames}
              canReconnect={canLink}
              onReconnect={() => setReconnectRequest("whatsapp")}
            />
          ) : null
        }
      />
    </div>
  );

  return (
    <div className={styles.page}>
      <div className={styles.sections}>
        <div className={styles.sectionWrapper}>{renderHeroRegion()}</div>

        {/* Only render the channels wrapper when there's an instance to attach
            them to. MessageChannelsSection returns null when tenantId is null
            (no claw), but the surrounding sectionWrapper would still draw its
            divider - leaving a stray line above Integrations on the empty
            state. */}
        {tenantId !== null ? channelsSection : null}

        <div className={styles.sectionWrapper}>
          <IntegrationsSection
            integrations={integrations}
            loading={integrationsStatus === "loading"}
            error={integrationsError}
          />
        </div>

        <div className={styles.sectionWrapper}>
          <ResourcesSection />
        </div>
      </div>
    </div>
  );
};
